import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQueryState, parseAsString } from "nuqs";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Users,
  User,
  Shield,
  Check,
  ChevronsUpDown,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Download,
  CalendarClock,
  FileText,
  History,
} from "lucide-react";
import { Plan, Employee, Dependent, type Enrollment } from "@shared/schema";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { isFsaConfigured } from "@/lib/fsaPreview";
import { useFsaConfigs } from "@/hooks/useFsaConfigs";
import { formatBenefitAmount } from "@shared/utils/enrollmentCalculator";
import { getRatingDate, createCoverageMonth, AgeDeterminationRule } from "@shared/lib/ageDetermination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn, formatCurrency as formatCurrencyUtil, formatDateDisplay, parseDateLocal, toYmdLocal } from "@/lib/utils";
import { calculateEnrollmentPremium } from "@/lib/enrollmentCalculator";
import { fetchPlanPremiumData } from "@/hooks/usePlanPremiumData";
import { EnrollmentHistoryDialog } from "@/components/enrollments/EnrollmentHistoryDialog";
import { PlanRate } from "@shared/schema";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Link } from "wouter";
import { exportToCSV } from "@/lib/exportUtils";
import { BulkEnrollmentFormsDialog } from "@/components/enrollment/BulkEnrollmentFormsDialog";
import { coverageTypeLabel } from "@/lib/coverageLabel";
import { Switch } from "@/components/ui/switch";
import { EnrollmentLike, isActiveDependentEnrollmentForPlan, isCurrentlyEffectiveEnrollment } from "@/lib/enrollmentFilters";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function getSavedEnrollmentsPageSize(clientId: string, fallback = 50): number {
  try {
    if (typeof window === "undefined") return fallback;
    const raw = window.localStorage.getItem(
      `administr.enrollments.pageSize:${clientId}`
    );
    const n = Number.parseInt(String(raw ?? ""), 10);
    const allowed = new Set([25, 50, 100, 200]);
    return Number.isFinite(n) && allowed.has(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function saveEnrollmentsPageSize(clientId: string, pageSize: number) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      `administr.enrollments.pageSize:${clientId}`,
      String(pageSize)
    );
  } catch {}
}

interface EnrollmentsTabProps {
  plansData: Plan[];
  clientEmployees: Employee[];
  clientId?: string;
  prospectId?: string;
  showCreate?: boolean;
  isEmployerPortal?: boolean; // Flag to indicate if used in employer portal context
  /**
   * When true, this component will render using the override props and will not
   * fetch data from the server. Useful for "UI-only" contexts like prospects.
   */
  disableFetch?: boolean;
  /** Optional overrides used when disableFetch=true */
  enrollmentsOverride?: Enrollment[];
  dependentsOverride?: Dependent[];
  planContributionsOverride?: any[];
  openEnrollmentOverride?: any | null;
}

const EnrollmentsTab = ({
  plansData,
  clientEmployees,
  clientId,
  prospectId,
  showCreate = true,
  isEmployerPortal = false,
  disableFetch = false,
  enrollmentsOverride,
  dependentsOverride,
  planContributionsOverride,
  openEnrollmentOverride,
}: EnrollmentsTabProps) => {
  const entityId = clientId || prospectId;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  // Legacy state: the create-enrollment dialog was removed, but its mutation
  // (still referenced below) closes via this setter.
  const [, setIsDialogOpen] = useState(false);
  const [oeElectionOpen, setOeElectionOpen] = useState(false);
  const [bulkFormsOpen, setBulkFormsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Enrollment> | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null
  );
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedEmployeeIds, setBulkSelectedEmployeeIds] = useState<
    number[]
  >([]);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [historyEnrollmentId, setHistoryEnrollmentId] = useState<number | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editIsTableMethod, setEditIsTableMethod] = useState<boolean>(false);
  // URL-based coverage tab persistence using nuqs
  const [coverageTab, setCoverageTab] = useQueryState(
    "coverageTab",
    parseAsString.withDefault("")
  );
  const [pageByTab, setPageByTab] = useState<Record<string, number>>({});
  const [searchByTab, setSearchByTab] = useState<Record<string, string>>({});
  const [pageSize, setPageSize] = useState<number>(() =>
    getSavedEnrollmentsPageSize(String(clientId), 50)
  );
  const [tabTotalPremium, setTabTotalPremium] = useState<Record<string, number>>(
    {}
  );
  const [tabTotalLoading, setTabTotalLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [showPayPeriod, setShowPayPeriod] = useState(false);
  const [enrollmentView, setEnrollmentView] = useState<
    "current" | "open_enrollment"
  >("current");

  // Fetch payroll settings for pay period calculation
  interface PayrollSettings {
    id: number;
    clientId: number;
    payFrequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';
    payDay: string | null;
    periodStartDate: string | null;
  }
  
  const { data: payrollSettings } = useQuery<PayrollSettings>({
    queryKey: ["/api/clients", clientId, "payroll-settings"],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${clientId}/payroll-settings`);
      if (!response.ok) throw new Error("Failed to fetch payroll settings");
      return response.json();
    },
    enabled: !disableFetch && !!clientId,
  });

  // Calculate pay periods per month based on frequency
  const payPeriodsPerMonth = useMemo(() => {
    const freq = payrollSettings?.payFrequency || 'biweekly';
    switch (freq) {
      case 'weekly': return 52 / 12; // ~4.33
      case 'biweekly': return 26 / 12; // ~2.17
      case 'semimonthly': return 2;
      case 'monthly': return 1;
      default: return 26 / 12;
    }
  }, [payrollSettings?.payFrequency]);

  // Helper function to get pay periods per month from employee's payCycle
  const getPayPeriodsForEmployee = (payCycle: string | null | undefined): number => {
    const cycle = (payCycle || '').toLowerCase().replace(/[-\s]/g, '');
    switch (cycle) {
      case 'weekly': return 52 / 12; // ~4.33
      case 'biweekly': return 26 / 12; // ~2.17
      case 'semimonthly': return 2;
      case 'monthly': return 1;
      case 'annually': return 1 / 12; // ~0.083
      default: return 26 / 12; // default to bi-weekly
    }
  };

  // Check if pay period is configured (not default)
  const isPayPeriodConfigured = payrollSettings?.id !== 0;

  const selectedEmployee = useMemo(
    () => clientEmployees.find((e) => e.id === selectedEmployeeId) || null,
    [clientEmployees, selectedEmployeeId]
  );
  const selectedPlan = useMemo(
    () => plansData.find((p) => p.id === selectedPlanId) || null,
    [plansData, selectedPlanId]
  );

  const selectedEmployeeClientId = useMemo(
    () => selectedEmployee?.clientId ?? null,
    [selectedEmployee]
  );

  const clientIdNum = useMemo(() => {
    // Strict parse: agency usage passes the agency UUID as clientId, and a
    // UUID starting with digits (e.g. "97a1ff49-…") must NOT be read as
    // client 97 — that mislabels the context and enables client-only
    // features against the wrong client.
    const s = String(clientId);
    if (!/^\d+$/.test(s)) return null;
    const n = Number.parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  }, [clientId]);

  // Client-level enrollments list
  const enrollmentsQuery = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments", clientId],
    enabled: !disableFetch,
    queryFn: async () => {
      // Premiums are computed server-side in bulk for large datasets.
      const res = await fetch(
        `/api/enrollments?clientId=${clientId}&computePremiums=1`
      );
      if (!res.ok) throw new Error("Failed to fetch enrollments");
      return res.json();
    },
  });
  const enrollmentsLoading = disableFetch ? false : enrollmentsQuery.isLoading;
  const clientEnrollments: Enrollment[] = (disableFetch
    ? (enrollmentsOverride ?? [])
    : (enrollmentsQuery.data ?? [])) as Enrollment[];

  // Open enrollment window for plan filtering and view selection
  const openEnrollmentQuery = useQuery<any>({
    queryKey: ["/api/clients", clientId, "open-enrollment"],
    enabled: !disableFetch,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/open-enrollment`);
      if (!res.ok) return null;
      return res.json();
    },
  });
  const openEnrollment = disableFetch
    ? openEnrollmentOverride ?? null
    : openEnrollmentQuery.data;

  const oeCoverageKey = useMemo(() => {
    const raw = (openEnrollment as any)?.startDate;
    if (!raw) return null;
    return String(raw).split("T")[0];
  }, [openEnrollment]);

  const isOpenEnrollmentActive = useMemo(() => {
    const start = openEnrollment?.startDate
      ? parseDateLocal(openEnrollment.startDate)
      : null;
    const end = openEnrollment?.endDate
      ? parseDateLocal(openEnrollment.endDate)
      : null;
    if (!start || !end) return false;
    const today = new Date();
    return today >= start && today <= end;
  }, [openEnrollment?.startDate, openEnrollment?.endDate]);

  // True when an OE window has been configured (regardless of active/closed)
  const hasOpenEnrollment = !!openEnrollment?.startDate;

  const enrollmentsForView = useMemo(() => {
    const list = Array.isArray(clientEnrollments)
      ? (clientEnrollments as any[])
      : [];
    if (!hasOpenEnrollment) return list;

    // "Current" = all enrollments (regardless of source)
    // "Open Enrollment" = only enrollments tagged as open_enrollment
    if (enrollmentView === "current") return list;

    return list.filter((e: any) => {
      const src = String(e?.enrollmentSource || "").toLowerCase();
      return src === "open_enrollment";
    });
  }, [clientEnrollments, enrollmentView, hasOpenEnrollment]);

  // Bulk dependents for this client (used for premium calculations)
  const clientDependentsQuery = useQuery<any[]>({
    queryKey: ["/api/dependents", clientIdNum],
    enabled: !disableFetch && Number.isFinite(clientIdNum as any),
    queryFn: async () => {
      const res = await fetch(`/api/dependents?clientId=${clientIdNum}`);
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const clientDependents: any[] = disableFetch
    ? (dependentsOverride ?? [])
    : ((clientDependentsQuery.data ?? []) as any[]);

  // Plan contributions are shared across the table; fetch once and filter per plan.
  const allPlanContributionsQuery = useQuery<any[]>({
    queryKey: ["/api/plan-contributions"],
    enabled: !disableFetch,
    queryFn: async () => {
      const res = await fetch(`/api/plan-contributions`);
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const allPlanContributions: any[] = disableFetch
    ? (planContributionsOverride ?? [])
    : ((allPlanContributionsQuery.data ?? []) as any[]);

  // FSA plan configs — flag FSA plans whose annual election limit isn't set
  // yet (mirrors the life "Coverage not configured" warning). Shared hook so
  // the fetch isn't duplicated.
  const { fsaConfigByPlanId, isFetched: fsaConfigsFetched } = useFsaConfigs();

  // OE changes report — fetched eagerly when an OE window exists
  const oeReportQuery = useQuery<any>({
    queryKey: ["/api/clients", clientId, "open-enrollment", "report", oeCoverageKey],
    enabled: !disableFetch && !!oeCoverageKey,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/open-enrollment/report?startDate=${oeCoverageKey}`);
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const activeBaseEnrollments = useMemo(() => {
    const filterFn =
      enrollmentView === "open_enrollment"
        ? (e: EnrollmentLike) => {
            const status = String(e?.status || "").toLowerCase();
            const isWaived = Boolean(e?.waivedCoverage);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endDate = e?.endDate ? parseDateLocal(e.endDate) : null;
            const ended = endDate ? endDate < today : false;
            return (status === "active" || status === "pending") && !isWaived && !ended;
          }
        : isCurrentlyEffectiveEnrollment;
    const filtered = (enrollmentsForView as any[])
      .filter((e) => (e as any).dependentId == null)
      .filter(filterFn);

    // Deduplicate: keep only the most recent base enrollment per (employeeId, planId)
    const best = new Map<string, any>();
    for (const e of filtered) {
      const key = `${e.employeeId}:${e.planId}`;
      const existing = best.get(key);
      if (!existing) {
        best.set(key, e);
        continue;
      }
      const eDate = e.effectiveDate ? (parseDateLocal(e.effectiveDate)?.getTime() ?? 0) : 0;
      const exDate = existing.effectiveDate ? (parseDateLocal(existing.effectiveDate)?.getTime() ?? 0) : 0;
      if (eDate > exDate || (eDate === exDate && e.id > existing.id)) {
        best.set(key, e);
      }
    }
    return Array.from(best.values());
  }, [enrollmentView, enrollmentsForView]);

  const planById = useMemo(() => {
    const m = new Map<number, any>();
    for (const p of plansData as any[]) {
      if (p?.id != null) m.set(Number(p.id), p);
    }
    return m;
  }, [plansData]);

  const employeeById = useMemo(() => {
    const m = new Map<number, any>();
    for (const e of clientEmployees as any[]) {
      if (e?.id != null) m.set(Number(e.id), e);
    }
    return m;
  }, [clientEmployees]);

  // Plans with live enrollments — the pick list for bulk enrollment-form download.
  const bulkFormPlanOptions = useMemo(() => {
    const counts = new Map<number, number>();
    for (const e of activeBaseEnrollments as any[]) {
      const planId = Number(e?.planId);
      if (!Number.isFinite(planId)) continue;
      counts.set(planId, (counts.get(planId) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([planId, employeeCount]) => ({
        planId,
        planName: planById.get(planId)?.name ?? `Plan ${planId}`,
        employeeCount,
      }))
      .sort((a, b) => a.planName.localeCompare(b.planName));
  }, [activeBaseEnrollments, planById]);

  const coverageTypes = useMemo(() => {
    const base = activeBaseEnrollments as any[];
    const types = Array.from(
      new Set(
        base
          .map((e) => planById.get(Number((e as any)?.planId))?.type)
          .filter(Boolean)
      )
    ) as string[];
    const preferred = [
      "health",
      "dental",
      "vision",
      "life",
      "disability",
      "std",
      "ltd",
      "fsa_health",
      "fsa_dependent_care",
      "transit",
      "voluntary",
    ];
    // Preferred order first, then any other discovered type appended — never
    // drop a type. Previously FSA types weren't in the list and fell through,
    // so enrolled FSA members had no tab and disappeared from this view.
    const ordered = [
      ...preferred.filter((t) => types.includes(t)),
      ...types.filter((t) => !preferred.includes(t)),
    ];
    return ordered.length > 0 ? ordered : types;
  }, [activeBaseEnrollments, planById]);

  // Keep a valid tab selected as data changes
  React.useEffect(() => {
    if (!coverageTab && coverageTypes.length > 0) {
      setCoverageTab(coverageTypes[0]);
      return;
    }
    if (coverageTab && coverageTypes.length > 0 && !coverageTypes.includes(coverageTab)) {
      setCoverageTab(coverageTypes[0]);
    }
  }, [coverageTab, coverageTypes]);

  const currentPage = pageByTab[coverageTab] || 1;
  const setCurrentPage = (next: number) =>
    setPageByTab((prev) => ({ ...prev, [coverageTab]: Math.max(1, next) }));

  const employeeSearchValue = useMemo(
    () => (searchByTab[coverageTab] ?? ""),
    [searchByTab, coverageTab]
  );

  // If the user navigates to a different client without a full refresh, reload the saved page size.
  React.useEffect(() => {
    setPageSize(getSavedEnrollmentsPageSize(String(clientId), 50));
  }, [clientId]);

  const handlePageSizeChange = (raw: string) => {
    const n = Number.parseInt(raw, 10);
    const next = Number.isFinite(n) ? n : 50;
    setPageSize(next);
    saveEnrollmentsPageSize(String(clientId), next);
    setCurrentPage(1);
  };

  const enrollmentsForTab = useMemo(() => {
    if (!coverageTab) return [];
    return (activeBaseEnrollments as any[]).filter((e) => {
      const plan = planById.get(Number((e as any)?.planId));
      return String(plan?.type || "") === String(coverageTab);
    });
  }, [activeBaseEnrollments, coverageTab, planById]);

  const filteredEnrollmentsForTab = useMemo(() => {
    const raw = String(employeeSearchValue || "").trim();
    const q = raw.toLowerCase();
    const norm = (v: any) => String(v ?? "").toLowerCase();
    const base = (enrollmentsForTab as any[]).filter((enr: any) => {
      if (!raw) return true;
      const employeeId = Number(enr?.employeeId);
      const emp = employeeById.get(employeeId);
      const plan = planById.get(Number(enr?.planId));
      const hay = [
        employeeId,
        emp?.firstName,
        emp?.lastName,
        `${emp?.firstName ?? ""} ${emp?.lastName ?? ""}`.trim(),
        emp?.department,
        emp?.email,
        plan?.name,
      ]
        .map(norm)
        .join(" ");
      return hay.includes(q);
    });

    // Keep ordering stable across edits by sorting alphabetically by employee name
    // (and then plan name). This prevents rows "jumping" when the backend order changes.
    const cmpText = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });
    const safeName = (v: any) => String(v ?? "").trim();
    return base.slice().sort((a: any, b: any) => {
      const aEmpId = Number(a?.employeeId);
      const bEmpId = Number(b?.employeeId);
      const aEmp = employeeById.get(aEmpId);
      const bEmp = employeeById.get(bEmpId);
      const aLast = safeName(aEmp?.lastName);
      const bLast = safeName(bEmp?.lastName);
      const byLast = cmpText(aLast, bLast);
      if (byLast) return byLast;

      const aFirst = safeName(aEmp?.firstName);
      const bFirst = safeName(bEmp?.firstName);
      const byFirst = cmpText(aFirst, bFirst);
      if (byFirst) return byFirst;

      // If names are missing or identical, fall back to id to keep deterministic.
      if (Number.isFinite(aEmpId) && Number.isFinite(bEmpId) && aEmpId !== bEmpId)
        return aEmpId - bEmpId;

      const aPlan = planById.get(Number(a?.planId));
      const bPlan = planById.get(Number(b?.planId));
      const byPlan = cmpText(safeName(aPlan?.name), safeName(bPlan?.name));
      if (byPlan) return byPlan;
      return Number(a?.planId) - Number(b?.planId);
    });
  }, [employeeSearchValue, employeeById, enrollmentsForTab, planById]);

  const waivedEmployeesForTab = useMemo(() => {
    if (!coverageTab) return [];
    const today = new Date();
    const rows = (enrollmentsForView as any[])
      .filter((e) => (e as any).dependentId == null)
      .filter((e: any) => {
        const status = String(e?.status || "").toLowerCase();
        const isWaived = Boolean(e?.waivedCoverage) || status === "waived";
        const ended = e?.endDate ? (parseDateLocal(e.endDate as any) ?? new Date(0)) <= today : false;
        return isWaived && !ended;
      })
      .filter((e: any) => {
        const plan = planById.get(Number((e as any)?.planId));
        return String(plan?.type || "") === String(coverageTab);
      });

    const byEmployee = new Map<number, any>();
    for (const r of rows) {
      const employeeId = Number((r as any)?.employeeId);
      if (!Number.isFinite(employeeId)) continue;
      if (!byEmployee.has(employeeId)) byEmployee.set(employeeId, r);
    }
    const out: any[] = [];
    for (const [employeeId, enrollment] of Array.from(byEmployee.entries())) {
      const emp = clientEmployees.find((e) => e.id === employeeId) as any;
      out.push({
        employeeId,
        employee: emp,
        enrollment,
        waiverReason: (enrollment as any)?.waiverReason || "",
      });
    }
    return out;
  }, [enrollmentsForView, clientEmployees, coverageTab, planById]);

  const filteredWaivedEmployeesForTab = useMemo(() => {
    const raw = String(employeeSearchValue || "").trim();
    const q = raw.toLowerCase();
    const norm = (v: any) => String(v ?? "").toLowerCase();
    const base = (waivedEmployeesForTab as any[]).filter((w: any) => {
      if (!raw) return true;
      const emp = w?.employee;
      const employeeId = Number(w?.employeeId);
      const hay = [
        employeeId,
        emp?.firstName,
        emp?.lastName,
        `${emp?.firstName ?? ""} ${emp?.lastName ?? ""}`.trim(),
        emp?.department,
        emp?.email,
        w?.waiverReason,
      ]
        .map(norm)
        .join(" ");
      return hay.includes(q);
    });

    const cmpText = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });
    const safeName = (v: any) => String(v ?? "").trim();
    return base.slice().sort((a: any, b: any) => {
      const aEmp = a?.employee;
      const bEmp = b?.employee;
      const byLast = cmpText(safeName(aEmp?.lastName), safeName(bEmp?.lastName));
      if (byLast) return byLast;
      const byFirst = cmpText(
        safeName(aEmp?.firstName),
        safeName(bEmp?.firstName)
      );
      if (byFirst) return byFirst;
      const aId = Number(a?.employeeId);
      const bId = Number(b?.employeeId);
      if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) return aId - bId;
      return 0;
    });
  }, [employeeSearchValue, waivedEmployeesForTab]);

  const participationRateForTab = useMemo(() => {
    const enrolledEmployeeIds = new Set<number>();
    for (const e of enrollmentsForTab as any[]) {
      const id = Number((e as any)?.employeeId);
      if (Number.isFinite(id)) enrolledEmployeeIds.add(id);
    }
    const waivedEmployeeIds = new Set<number>();
    for (const w of waivedEmployeesForTab as any[]) {
      const id = Number((w as any)?.employeeId);
      if (Number.isFinite(id)) waivedEmployeeIds.add(id);
    }
    const total = enrolledEmployeeIds.size + waivedEmployeeIds.size;
    if (total === 0) return 0;
    return Math.round((enrolledEmployeeIds.size / total) * 100);
  }, [enrollmentsForTab, waivedEmployeesForTab]);

  const totalForTab = filteredEnrollmentsForTab.length;
  const totalPages = Math.max(1, Math.ceil(totalForTab / Math.max(1, pageSize)));
  const clampedPage = Math.min(currentPage, totalPages);

  React.useEffect(() => {
    if (clampedPage !== currentPage) setCurrentPage(clampedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedPage, currentPage, totalPages]);

  const visibleEnrollments = useMemo(() => {
    const start = (clampedPage - 1) * pageSize;
    return filteredEnrollmentsForTab.slice(start, start + pageSize);
  }, [clampedPage, filteredEnrollmentsForTab, pageSize]);

  const uniquePlanIdsForTab = useMemo(() => {
    const ids = new Set<number>();
    for (const e of filteredEnrollmentsForTab as any[]) {
      const pid = (e as any)?.planId;
      if (Number.isFinite(pid)) ids.add(pid as number);
    }
    return Array.from(ids.values());
  }, [filteredEnrollmentsForTab]);

  const uniqueActivePlanIds = useMemo(() => {
    const ids = new Set<number>();
    for (const e of visibleEnrollments as any[]) {
      const pid = (e as any)?.planId;
      if (Number.isFinite(pid)) ids.add(pid as number);
    }
    return Array.from(ids.values());
  }, [visibleEnrollments]);

  const planRatesQueries = useQueries({
    queries: uniquePlanIdsForTab.map((planId) => ({
      queryKey: ["/api/plan-rates", planId, clientIdNum],
      queryFn: async () => {
        const url =
          clientIdNum != null
            ? `/api/plan-rates/${planId}?clientId=${clientIdNum}`
            : `/api/plan-rates/${planId}`;
        const res = await fetch(url);
        if (!res.ok) return [] as PlanRate[];
        const json = await res.json();
        return ((json?.rates || json || []) as PlanRate[]) ?? [];
      },
      enabled: Number.isFinite(planId as any),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });

  // React Query may keep the same array/object identity while updating fields like
  // isFetching/data. This signature ensures downstream memo/effects re-run when
  // any plan-rates query changes state or data.
  const planRatesStatusKey = planRatesQueries
    .map(
      (q: any) =>
        `${q?.status ?? ""}:${q?.fetchStatus ?? ""}:${q?.dataUpdatedAt ?? 0}`
    )
    .join("|");

  const planRatesByPlanId = useMemo(() => {
    const m = new Map<number, PlanRate[]>();
    for (let i = 0; i < uniquePlanIdsForTab.length; i++) {
      const planId = uniquePlanIdsForTab[i];
      const q = planRatesQueries[i];
      const rates = (q?.data || []) as PlanRate[];
      m.set(planId, rates);
    }
    return m;
  }, [planRatesStatusKey, uniquePlanIdsForTab]);

  const planRatesMetaByPlanId = useMemo(() => {
    const m = new Map<number, { status: string; fetchStatus: string; dataUpdatedAt: number }>();
    for (let i = 0; i < uniquePlanIdsForTab.length; i++) {
      const planId = uniquePlanIdsForTab[i];
      const q: any = planRatesQueries[i];
      m.set(planId, {
        status: String(q?.status ?? ""),
        fetchStatus: String(q?.fetchStatus ?? ""),
        dataUpdatedAt: Number(q?.dataUpdatedAt ?? 0) || 0,
      });
    }
    return m;
  }, [planRatesStatusKey, uniquePlanIdsForTab]);

  const planContribsByPlanId = useMemo(() => {
    const m = new Map<number, any[]>();
    const list = Array.isArray(allPlanContributions)
      ? allPlanContributions
      : [];
    for (const c of list) {
      const pid = Number((c as any)?.planId);
      if (!Number.isFinite(pid)) continue;
      const arr = m.get(pid) || [];
      arr.push(c);
      m.set(pid, arr);
    }
    return m;
  }, [allPlanContributions]);

  const dependentsByEmployeeId = useMemo(() => {
    const m = new Map<number, any[]>();
    const deps = Array.isArray(clientDependents) ? clientDependents : [];
    for (const d of deps) {
      const eid = Number((d as any)?.employeeId);
      if (!Number.isFinite(eid)) continue;
      const arr = m.get(eid) || [];
      arr.push(d);
      m.set(eid, arr);
    }
    return m;
  }, [clientDependents]);

  const dependentByIdByEmployeeId = useMemo(() => {
    const m = new Map<number, Map<number, any>>();
    for (const [eid, deps] of Array.from(dependentsByEmployeeId.entries())) {
      const inner = new Map<number, any>();
      for (const d of deps || []) {
        const id = Number((d as any)?.id);
        if (!Number.isFinite(id)) continue;
        inner.set(id, d);
      }
      m.set(eid, inner);
    }
    return m;
  }, [dependentsByEmployeeId]);

  const dependentIdsByEmployeePlan = useMemo(() => {
    const m = new Map<string, number[]>();
    const list = Array.isArray(enrollmentsForView)
      ? (enrollmentsForView as any[])
      : [];
    const seen = new Map<string, Set<number>>();
    for (const r of list) {
      const depId = (r as any)?.dependentId;
      if (!Number.isFinite(depId)) continue;
      const employeeId = (r as any)?.employeeId;
      const planId = (r as any)?.planId;
      if (!Number.isFinite(employeeId) || !Number.isFinite(planId)) continue;
      const key = `${employeeId}:${planId}`;
      const s = seen.get(key) || new Set<number>();
      if (s.has(depId as number)) continue;
      s.add(depId as number);
      seen.set(key, s);
      const arr = m.get(key) || [];
      arr.push(depId as number);
      m.set(key, arr);
    }
    return m;
  }, [enrollmentsForView]);

  const calcAgeAsOf = (plan: any, employee: any) => {
    const planEffective = plan?.effectiveDate ? parseDateLocal(plan.effectiveDate) : null;
    const hireDate = employee?.hireDate ? parseDateLocal(employee.hireDate) : null;
    let ageAsOf: Date | undefined = undefined;
    if (planEffective && hireDate) {
      ageAsOf = hireDate < planEffective ? planEffective : hireDate;
    } else if (planEffective) {
      ageAsOf = planEffective;
    } else if (hireDate) {
      ageAsOf = hireDate;
    }
    return ageAsOf;
  };

  const premiumResultByEnrollmentId = useMemo(() => {
    const out = new Map<number, any>();
    const base = Array.isArray(visibleEnrollments)
      ? (visibleEnrollments as any[])
      : [];

    for (const enr of base) {
      const enrId = Number((enr as any)?.id);
      if (!Number.isFinite(enrId)) continue;
      // Preferred: server-computed premium payload (fast, consistent across pages)
      const p = (enr as any)?.premium;
      if (p) {
        out.set(enrId, p);
        continue;
      }
    }

    return out;
  }, [visibleEnrollments]);

  // Compute tab-wide total premium in chunks (keeps UI responsive on large datasets)
  React.useEffect(() => {
    if (!coverageTab) return;
    let cancelled = false;

    const run = async () => {
      setTabTotalLoading((prev) => ({ ...prev, [coverageTab]: true }));
      try {
        let sum = 0;
        const base = Array.isArray(enrollmentsForTab)
          ? (enrollmentsForTab as any[])
          : [];
        const chunkSize = 250;
        for (let i = 0; i < base.length; i += chunkSize) {
          if (cancelled) return;
          const chunk = base.slice(i, i + chunkSize);
          for (const enr of chunk) {
            const monthly = Number((enr as any)?.premium?.totalMonthlyRate || 0);
            if (Number.isFinite(monthly)) sum += monthly;
          }
          // Yield to the browser
          await new Promise((r) => setTimeout(r, 0));
        }
        if (cancelled) return;
        setTabTotalPremium((prev) => ({ ...prev, [coverageTab]: sum }));
      } finally {
        if (!cancelled)
          setTabTotalLoading((prev) => ({ ...prev, [coverageTab]: false }));
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    coverageTab,
    enrollmentsForTab,
  ]);

  const hasActiveEnrollment = (
    employeeId?: number | null,
    planId?: number | null
  ) => {
    if (!employeeId || !planId) return false;
    return activeBaseEnrollments.some(
      (e: any) => e.employeeId === employeeId && e.planId === planId
    );
  };

  // Filter plan options based on OE and existing enrollments
  const filteredPlanOptions = useMemo(() => {
    const today = new Date();
    const hasOE = !!openEnrollment?.startDate;
    const hasExistingEnrollments = (clientEnrollments || []).length > 0;
    const hasFuturePlans = (plansData || []).some((p: any) =>
      p?.effectiveDate ? (parseDateLocal(p.effectiveDate) ?? new Date(0)) > today : false
    );

    const oeSelectedIds: number[] = Array.isArray(
      (openEnrollment as any)?.selectedPlanIds
    )
      ? ((openEnrollment as any).selectedPlanIds as number[])
      : [];

    let list = (plansData || []).filter(
      (p: any) => (p?.isActive ?? true) !== false
    );

    // If OE has specific plans selected, only show those
    if (hasOE && oeSelectedIds.length > 0) {
      list = list.filter((p: any) => oeSelectedIds.includes(p.id as any));
    }

    const inForceAsOf = (ref: Date) =>
      list.filter((p: any) => {
        const eff = p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null;
        const end = p?.endDate ? parseDateLocal(p.endDate) : null;
        const startsOk = !eff || eff <= ref;
        const notEnded = !end || end > ref;
        return startsOk && notEnded;
      });

    if (hasOE) {
      const refDate = parseDateLocal(openEnrollment.startDate) ?? new Date();

      // First try to find plans that will be in force during the OE period
      const renewingPlans = list.filter((p: any) => {
        const eff = p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null;
        const end = p?.endDate ? parseDateLocal(p.endDate) : null;

        // Include plans with no effective date (legacy plans)
        if (!eff) return true;

        // Include plans that start on or before the OE coverage date
        const startsInOrBeforeOE = eff <= refDate;
        const notEnded = !end || end > refDate;
        return startsInOrBeforeOE && notEnded;
      });
      return renewingPlans;
    }

    // Default: Show all active, non-expired plans
    // This allows brokers to manually enroll employees in any available plan
    const result = list.filter((p: any) => {
      const end = p?.endDate ? parseDateLocal(p.endDate) : null;
      const notEnded = !end || end > today;
      return notEnded;
    });
    return result;
  }, [plansData, openEnrollment?.startDate, clientEnrollments]);

  const formatCurrencyLocal = (n: number | string | null | undefined) => {
    const v = Number.parseFloat(String(n ?? 0));
    if (!Number.isFinite(v)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      currencyDisplay: "narrowSymbol",
    }).format(v);
  };

  const calcAge = (dob?: string | Date | null) => {
    if (!dob) return 0;
    const d = parseDateLocal(dob as any);
    if (!d || Number.isNaN(d.getTime())) return 0;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return Math.max(0, age);
  };

  const getCoveredDependentsFor = (employeeId: number, planId: number) => {
    const depKey = `${employeeId}:${planId}`;
    const depIds = dependentIdsByEmployeePlan.get(depKey) || [];
    const depById = dependentByIdByEmployeeId.get(employeeId) || new Map<number, any>();
    return (depIds || []).map((id) => depById.get(Number(id))).filter(Boolean);
  };

  const getDependentEnrollmentRow = (
    employeeId: number,
    planId: number,
    dependentId: number
  ) => {
    const list = Array.isArray(enrollmentsForView)
      ? (enrollmentsForView as any[])
      : [];
    return list.find(
      (r) =>
        Number((r as any)?.employeeId) === employeeId &&
        Number((r as any)?.planId) === planId &&
        Number((r as any)?.dependentId) === dependentId
    );
  };

  const getMonthlyForAgeBand = (planId: number, personDob?: any, planData?: Plan | null, coverageStartDate?: Date | string | null) => {
    const rates = planRatesByPlanId.get(planId) || [];
    if (!personDob || rates.length === 0) return null;

    // Use age-banded rows when present (TABLE/WB_TABLE). If not, we can't reliably
    // compute an individual dependent premium.
    const byAge = new Map<string, any>();
    for (const r of rates as any[]) {
      if ((r as any)?.ageBand) byAge.set(String((r as any).ageBand), r);
    }
    if (byAge.size === 0) return null;

    // Calculate the rating date based on the plan's age determination rule
    let asOf = new Date();
    
    if (planData && personDob) {
      const ageDetermination = (planData as any).ageDetermination || AgeDeterminationRule.PLAN_ANNIVERSARY;
      const planEffective = (planData as any).effectiveDate
        ? parseDateLocal((planData as any).effectiveDate) ?? new Date()
        : new Date();
      const coverageStart = coverageStartDate
        ? parseDateLocal(coverageStartDate as any) ?? planEffective
        : planEffective;
      
      try {
        const coverageMonth = createCoverageMonth(coverageStart.getFullYear(), coverageStart.getMonth() + 1);
        const ratingDate = getRatingDate(
          ageDetermination,
          { dob: personDob, personCoverageStartDate: coverageStart },
          coverageMonth,
          { planEffectiveDate: planEffective, ageDetermination }
        );
        if (ratingDate) {
          asOf = ratingDate;
        }
      } catch {
        // Fallback to current date if getRatingDate fails
      }
    }

    const age = (() => {
      const d = parseDateLocal(personDob);
      if (!d || Number.isNaN(d.getTime())) return 0;
      let a = asOf.getFullYear() - d.getFullYear();
      const m = asOf.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && asOf.getDate() < d.getDate())) a--;
      return Math.max(0, a);
    })();

    const parseBand = (label: string) => {
      const s = String(label || "").trim();
      if (/^\d+\s*-\s*\d+$/i.test(s)) {
        const [a, b] = s
          .split("-")
          .map((x) => Number.parseInt(x.trim(), 10));
        return { min: a, max: b as number | null } as const;
      }
      if (/^\d+\+$/i.test(s)) {
        const a = Number.parseInt(s.replace("+", "").trim(), 10);
        return { min: a, max: null as number | null } as const;
      }
      const n = Number.parseInt(s, 10);
      if (Number.isFinite(n)) return { min: n, max: n as number | null } as const;
      return { min: 0, max: null as number | null } as const;
    };

    const findRowForAge = (a: number) => {
      for (const [label, r] of Array.from(byAge.entries())) {
        const { min, max } = parseBand(label);
        const within = max == null ? a >= min : a >= min && a <= max;
        if (within) return r as any;
      }
      return null;
    };

    const row = findRowForAge(age);
    if (!row) return null;
    const monthly = Number.parseFloat(String((row as any).monthlyRate || "0"));
    return Number.isFinite(monthly) ? monthly : null;
  };
  const formatDate = (d: string | Date | null | undefined) => {
    if (!d) return "-";
    const dt = typeof d === "string" ? parseDateLocal(d) : d;
    if (!dt || Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleDateString();
  };

  async function fetchPlanRatesAndContribs(planId: number, clientId?: number) {
    // Includes the referenced base plan's rates/contributions so buy-up
    // employer contributions resolve (allPlanRates/allPlanContributions).
    const pd = await fetchPlanPremiumData([planId], clientId);
    return {
      planRates: (pd.ratesByPlanId[planId] || []) as PlanRate[],
      planContribs: pd.contribsByPlanId[planId] || [],
      allPlanRates: pd.allPlanRates,
      allPlanContributions: pd.allPlanContributions,
    };
  }

  const buildExportRows = async () => {
    const base = activeBaseEnrollments;
    const employeeIds = Array.from(
      new Set(base.map((b) => (b as any).employeeId as number))
    );
    const dependentsByEmployee = new Map<number, any[]>();
    await Promise.all(
      employeeIds.map(async (id) => {
        try {
          const res = await fetch(`/api/employees/${id}/dependents`);
          const deps = res.ok ? await res.json() : [];
          dependentsByEmployee.set(id, deps || []);
        } catch {
          dependentsByEmployee.set(id, []);
        }
      })
    );

    // Preload rates + contributions for ALL enrolled plans (and their base
    // plans) in ONE pass, rather than re-fetching the full contributions table
    // per enrollment inside the loop below.
    const exportPlanIds = Array.from(
      new Set(base.map((e) => Number((e as any).planId)).filter(Number.isFinite)),
    );
    const premiumData = await fetchPlanPremiumData(exportPlanIds, clientIdNum);

    const rows: Array<Record<string, any>> = [];
    for (const enr of base) {
      const emp = clientEmployees.find((e) => e.id === (enr as any).employeeId);
      const plan = plansData.find((p) => p.id === (enr as any).planId) as any;
      if (!emp || !plan) continue;

      // Covered dependents — exclude waived dep rows (they declined coverage).
      const allDeps = dependentsByEmployee.get(emp.id as number) || [];
      const enrolledDepIds = (enrollmentsForView as any[])
        .filter(
          (r) =>
            r.employeeId === (enr as any).employeeId &&
            isActiveDependentEnrollmentForPlan(r, (enr as any).planId),
        )
        .map((r) => r.dependentId as number);
      const coveredDependents = (allDeps as any[]).filter((d) =>
        enrolledDepIds.includes(d.id)
      );

      // Age reference date rule
      const planEffective = plan?.effectiveDate
        ? parseDateLocal(plan.effectiveDate)
        : null;
      const hireDate = emp?.hireDate ? parseDateLocal(emp.hireDate) : null;
      let ageAsOf: Date | undefined = undefined;
      if (planEffective && hireDate) {
        ageAsOf = hireDate < planEffective ? planEffective : hireDate;
      } else if (planEffective) {
        ageAsOf = planEffective;
      } else if (hireDate) {
        ageAsOf = hireDate;
      }

      const planRates = (premiumData.ratesByPlanId[plan.id] || []) as PlanRate[];
      const planContribs = premiumData.contribsByPlanId[plan.id] || [];
      const allPlanRates = premiumData.allPlanRates;
      const allPlanContributions = premiumData.allPlanContributions;
      const result = calculateEnrollmentPremium({
        plan,
        planRates,
        planContributions: planContribs as any,
        employee: emp as any,
        dependents: coveredDependents,
        coverageTier: (enr as any).coverageTier,
        ageAsOf,
        allPlanContributions,
        allPlanRates,
      }) as any;
      const method = (result?.contributionResolved?.method || "").toString();
      const coverageLabel =
        method === "table_rates"
          ? `Table: ${result?.contributionResolved?.key ?? "Unknown"}`
          : String((enr as any).coverageTier || "").replace(/_/g, " ");

      // Helper to calculate age for export
      const calcAge = (dob?: string | Date | null) => {
        if (!dob) return 0;
        const d = parseDateLocal(dob as any);
        if (!d || Number.isNaN(d.getTime())) return 0;
        const now = new Date();
        let age = now.getFullYear() - d.getFullYear();
        const m = now.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
        return Math.max(0, age);
      };

      rows.push({
        Employee: `${emp.firstName} ${emp.lastName}`,
        Dependents: (coveredDependents || [])
          .map((d: any) => {
            const age = calcAge(d.dateOfBirth);
            return `${d.firstName} ${d.lastName} (${d.relationship}, Age: ${age})`;
          })
          .join("; "),
        Plan: `${plan.name}`,
        "Coverage Type": coverageLabel,
        Carrier: plan.carrier || "",
        Rate: result.totalMonthlyRate,
        "Employer Contribution": result.employerPremium,
        "Employee Contribution": result.employeePremium,
      });
    }
    return rows;
  };

  const handleExportCSV = async () => {
    const rows = await buildExportRows();
    exportToCSV(rows as any[], {
      filename: `enrollments_${new Date().toISOString().slice(0, 10)}`,
      toast: { success: "Enrollments exported" },
    });
  };

  const handleExportPDF = async () => {
    const rows = await buildExportRows();
    const w = window.open("", "_blank");
    if (!w) return;
    const style = `
      <style>
        body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; padding: 16px; }
        h1 { font-size: 18px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #f3f4f6; }
      </style>`;
    const headers = [
      "Employee",
      "Dependents",
      "Plan",
      "Coverage Type",
      "Carrier",
      "Rate",
      "Employer Contribution",
      "Employee Contribution",
    ];
    const rowsHtml = rows
      .map((r: any) => {
        const cells = headers
          .map(
            (h) =>
              `<td>${
                typeof r[h] === "number"
                  ? formatCurrencyUtil(r[h])
                  : String(r[h] ?? "")
              }</td>`
          )
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"/>${style}</head><body>
      <h1>Employee Enrollments</h1>
      <table><thead><tr>${headers
        .map((h) => `<th>${h}</th>`)
        .join("")}</tr></thead><tbody>${rowsHtml}</tbody></table>
    </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleExportOeReport = () => {
    const report = oeReportQuery.data as any;
    if (!report?.rows?.length) {
      toast({ title: "No OE data to export", variant: "destructive" });
      return;
    }

    // Build a lookup of premium data from active enrollments (already computed with computePremiums=1)
    const premiumLookup = new Map<string, any>();
    for (const enr of activeBaseEnrollments as any[]) {
      const key = `${enr.employeeId}:${enr.planId}`;
      premiumLookup.set(key, enr);
    }

    const fmtCur = (val: number | null | undefined) => val != null ? formatCurrencyUtil(val) : "";

    const rows: Array<Record<string, any>> = [];
    for (const row of report.rows) {
      if (!row.planChanges?.length) {
        rows.push({
          "Employee Name": row.employeeName,
          Department: row.department || "",
          "OE Status": row.oeStatus,
          "Change Type": "No Change",
          "Plan Type": "",
          "Previous Plan": "",
          "Current Plan": "",
          "Previous Carrier": "",
          "Current Carrier": "",
          "Previous Tier": "",
          "Current Tier": "",
          "Previous Monthly Rate": "",
          "Current Monthly Rate": "",
          "Previous Employee Contribution": "",
          "Current Employee Contribution": "",
          "Previous Employer Contribution": "",
          "Current Employer Contribution": "",
          "Dependents Covered": "",
          "Waiver Reason": "",
        });
        continue;
      }
      for (const pc of row.planChanges) {
        const enr = premiumLookup.get(`${row.employeeId}:${pc.planId}`);
        rows.push({
          "Employee Name": row.employeeName,
          Department: row.department || "",
          "OE Status": row.oeStatus,
          "Change Type": pc.changeType.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
          "Plan Type": pc.planType || "",
          "Previous Plan": pc.previousPlanName || "",
          "Current Plan": pc.planName,
          "Previous Carrier": pc.previousCarrierName || "",
          "Current Carrier": pc.carrierName || "",
          "Previous Tier": pc.previousTier || "",
          "Current Tier": pc.currentTier || "",
          "Previous Monthly Rate": fmtCur(pc.previousTotalPremium),
          "Current Monthly Rate": enr?.monthlyRate != null ? formatCurrencyUtil(enr.monthlyRate) : fmtCur(pc.currentTotalPremium),
          "Previous Employee Contribution": fmtCur(pc.previousEmployeePremium),
          "Current Employee Contribution": enr?.employeePremium != null ? formatCurrencyUtil(enr.employeePremium) : fmtCur(pc.currentEmployeePremium),
          "Previous Employer Contribution": fmtCur(pc.previousEmployerPremium),
          "Current Employer Contribution": enr?.employerPremium != null ? formatCurrencyUtil(enr.employerPremium) : fmtCur(pc.currentEmployerPremium),
          "Dependents Covered": (pc.dependentsCovered || []).join("; "),
          "Waiver Reason": pc.waiverReason || "",
        });
      }
    }

    exportToCSV(rows, {
      filename: `oe-changes-and-contributions-${oeCoverageKey || "report"}`,
      toast: { success: "OE report exported" },
    });
  };

  const { data: dependents = [] } = useQuery<Dependent[]>({
    queryKey: [`/api/employees/${selectedEmployeeId}/dependents`],
    queryFn: async () => {
      const res = await fetch(
        `/api/employees/${selectedEmployeeId}/dependents`
      );
      if (!res.ok) throw new Error("Failed to fetch dependents");
      return res.json();
    },
    enabled: !!selectedEmployeeId,
  });

  const createEnrollment = useMutation({
    mutationFn: async (data: any) => {
      // DEV-262: removed ~150 lines here that re-computed employee/employer/
      // total premiums with an inline rating engine (own age-band parser and
      // a 50/50 fallback split). The results were never sent — the payload
      // relies on server-side premium computation — so it was dead work on
      // every enrollment create.

      // Determine effectiveDate to send based on plan vs hire date rule
      let effectiveDate: string = new Date().toISOString();
      try {
        const emp = bulkMode
          ? clientEmployees.find((e) => e.id === data.employeeId)
          : selectedEmployee;
        const planEff = (selectedPlan as any)?.effectiveDate
          ? parseDateLocal((selectedPlan as any).effectiveDate)
          : null;
        const hire = emp?.hireDate ? parseDateLocal(emp.hireDate as any) : null;
        const chosen =
          planEff && hire ? (hire < planEff ? planEff : hire) : planEff || hire;
        if (chosen && !Number.isNaN(chosen.getTime()))
          effectiveDate = chosen.toISOString();
      } catch {}

      const payload = {
        employeeId: data.employeeId,
        planId: data.planId,
        clientId: selectedEmployeeClientId,
        coverageTier: data.coverageTier,
        effectiveDate,
        // Premiums are computed dynamically server-side; do not persist
        beneficiaryName: data.beneficiaryName || null,
        beneficiaryRelationship: data.beneficiaryRelationship || null,
        waivedCoverage: !!data.waivedCoverage,
        waiverReason: data.waiverReason || null,
        enrollmentSource: data.enrollmentSource || "open_enrollment",
        status: data.waivedCoverage ? "waived" : "active",
        // New: include selected dependent IDs from wizard
        dependentIds: Array.isArray(data?.dependents) ? data.dependents : [],
      };

      const res = await fetch(`/api/employees/${data.employeeId}/enrollments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to create enrollment");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Enrollment created" , variant: "success"});
      setIsDialogOpen(false);
      setSelectedEmployeeId(null);
      setSelectedPlanId(null);
      queryClient.invalidateQueries({
        queryKey: ["/api/employees", selectedEmployeeId, "benefits"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/enrollments", clientId],
      });
    },
    onError: (e: any) => {
      toast({
        title: e?.message || "Enrollment failed",
        variant: "destructive",
      });
    },
  });

  const handleComplete = (data: any) => {
    createEnrollment.mutate(data);
  };

  const updateEnrollment = useMutation({
    mutationFn: async (updates: any) => {
      if (!editing || !(editing as any).id)
        throw new Error("No enrollment selected");
      const id = (editing as any).id;
      const res = await fetch(`/api/enrollments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to update enrollment");
      }
      return res.json();
    },
    onError: (e: any) => {
      toast({ title: e?.message || "Update failed", variant: "destructive" });
    },
  });

  const deleteEnrollment = useMutation({
    mutationFn: async (enrollment: any) => {
      const employeeId = enrollment?.employeeId as number | undefined;
      const planId = enrollment?.planId as number | undefined;
      const dependentId = enrollment?.dependentId as number | undefined;

      // If this is a dependent enrollment row, only delete that one row.
      if (Number.isFinite(dependentId as any)) {
        if (!Number.isFinite(enrollment?.id as any)) {
          throw new Error("Missing enrollment id for dependent removal");
        }
        const res = await fetch(`/api/enrollments/${enrollment.id}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error("Failed to remove dependent");
        return;
      }

      if (!employeeId || !planId) {
        throw new Error("Missing employee or plan for deletion");
      }

      let rows: any[] = [];
      try {
        const res = await fetch(`/api/enrollments?employeeId=${employeeId}`);
        rows = res.ok ? await res.json() : [];
      } catch {
        rows = [];
      }

      const related = (rows as any[]).filter(
        (r) => r.planId === planId && Number.isFinite(r.id)
      );

      const targets = related.length > 0 ? related : [enrollment];

      const responses = await Promise.all(
        targets.map((r) =>
          fetch(`/api/enrollments/${r.id}`, {
            method: "DELETE",
          })
        )
      );

      const failed = responses.find((r) => !r.ok);
      if (failed) {
        throw new Error("Failed to delete enrollment");
      }
    },
    onSuccess: (_data, enrollment: any) => {
      const employeeId = enrollment?.employeeId;
      toast({ title: "Enrollment deleted", variant: "success" });
      if (employeeId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/employees", employeeId, "benefits"],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/enrollments", clientId],
      });
    },
    onError: (e: any) => {
      toast({
        title: e?.message || "Failed to delete enrollment",
        variant: "destructive",
      });
    },
  });

  const [editCoverageTier, setEditCoverageTier] = useState<string>("");
  const [editEffectiveDate, setEditEffectiveDate] = useState<string>("");
  const [editWaived, setEditWaived] = useState<boolean>(false);
  const [editBeneficiaryName, setEditBeneficiaryName] = useState<string>("");
  const [editBeneficiaryRelationship, setEditBeneficiaryRelationship] =
    useState<string>("");
  const [editSaving, setEditSaving] = useState<boolean>(false);

  // Open edit dialog and seed form fields
  const openEdit = (enr: any) => {
    setEditing(enr);
    setEditCoverageTier(String(enr.coverageTier || "employee_only"));
    setEditEffectiveDate(
      enr.effectiveDate
        ? toYmdLocal(enr.effectiveDate) ?? new Date().toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)
    );
    setEditWaived(!!enr.waivedCoverage);
    setEditBeneficiaryName(enr.beneficiaryName || "");
    setEditBeneficiaryRelationship(enr.beneficiaryRelationship || "");
    // Determine if this plan uses table contributions with age/table rates
    (async () => {
      try {
        const { planRates, planContribs } = await fetchPlanRatesAndContribs(
          enr.planId,
          enr.clientId
        );
        const rateType = String(
          (planRates?.[0] as any)?.rateStructureType || ""
        );
        const hasTable = (planContribs as any[]).some(
          (c: any) => String(c.contributionType) === "table_rates"
        );
        const isTable =
          hasTable &&
          ["age_rates", "wb_table", "table_rates"].includes(rateType);
        setEditIsTableMethod(!!isTable);
      } catch {
        setEditIsTableMethod(false);
      }
    })();
    setIsEditOpen(true);
  };

  const selectedEmployeeHasActiveEnrollment =
    !bulkMode &&
    selectedEmployee &&
    selectedPlan &&
    hasActiveEnrollment(selectedEmployee.id as number, selectedPlan.id as number);

  const isEmployeeEligibleForSelectedPlan = (emp: any) => {
    if (!emp) return false;
    // Filter out employees who already have ANY active enrollment in ANY plan
    return !activeBaseEnrollments.some(
      (e: any) => e.employeeId === emp.id
    );
  };

  return (
    // Add a bottom safe-area so fixed UI (chat bubble) doesn't overlap the last row,
    // without creating a big empty gap inside the waived section itself.
    <div className="space-y-4 pb-28">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-medium">Employee Enrollments</h4>
        <div className="flex items-center gap-2">
          {/* isOpenEnrollmentActive (window open today), not hasOpenEnrollment
              (a window was ever configured, true forever after). The report
              button below keeps hasOpenEnrollment on purpose - reviewing a
              closed window, not switching into it. */}
          {isOpenEnrollmentActive && (
            <Select
              value={enrollmentView}
              onValueChange={(v) =>
                setEnrollmentView(v as "current" | "open_enrollment")
              }
            >
              <SelectTrigger className="w-[200px]" aria-label="Enrollment view">
                <SelectValue placeholder="Enrollment view" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current">Current Enrollment</SelectItem>
                <SelectItem value="open_enrollment">Open Enrollment</SelectItem>
              </SelectContent>
            </Select>
          )}
          {hasOpenEnrollment && (
            <Button
              variant="outline"
              onClick={handleExportOeReport}
              disabled={oeReportQuery.isLoading || !oeReportQuery.data?.rows?.length}
            >
              <Download className="h-4 w-4 mr-1" />
              Download OE Changes Report
            </Button>
          )}
          {showCreate && (
            <Button variant="outline" onClick={() => setOeElectionOpen(true)}>
              <CalendarClock className="h-4 w-4 mr-1" />
              Future Election (OE)
            </Button>
          )}
          {clientIdNum != null && bulkFormPlanOptions.length > 0 && (
            <Button variant="outline" onClick={() => setBulkFormsOpen(true)}>
              <FileText className="h-4 w-4 mr-1" />
              Enrollment Forms
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF}>
            Export PDF
          </Button>
        </div>
      </div>

      {enrollmentsLoading ? (
        <Card>
          <CardContent className="py-6">Loading enrollments…</CardContent>
        </Card>
      ) : enrollmentsForView.length > 0 ? (
        <div className="space-y-4">
          {(() => {
            if (!coverageTypes || coverageTypes.length === 0) return null;
            const label = coverageTypeLabel;
            const start = totalForTab === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
            const end = Math.min(totalForTab, clampedPage * pageSize);
            return (
              <Tabs
                value={coverageTab}
                onValueChange={(v) => {
                  setCoverageTab(v);
                  setPageByTab((prev) => ({ ...prev, [v]: 1 }));
                }}
              >
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <TabsList>
                    {coverageTypes.map((t) => (
                      <TabsTrigger key={t} value={t} className="text-xs h-7">
                        {label(t)} Coverage
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="w-full sm:w-[280px]">
                    <Input
                      value={employeeSearchValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchByTab((prev) => ({ ...prev, [coverageTab]: v }));
                        setCurrentPage(1);
                      }}
                      placeholder="Search employees…"
                      aria-label="Search employees"
                    />
                  </div>
                </div>

                {coverageTypes.map((t) => (
                  <TabsContent key={t} value={t} className="mt-0">
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <CardTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            {label(t)} Coverage
                          </CardTitle>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-muted-foreground">
                              Total Premium
                            </div>
                            <div className="text-xl font-semibold">
                              {tabTotalLoading[t]
                                ? "…"
                                : formatCurrencyUtil(tabTotalPremium[t] || 0)}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Pay Period Toggle */}
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-sm text-muted-foreground">Monthly</span>
                          <Switch
                            checked={showPayPeriod}
                            onCheckedChange={setShowPayPeriod}
                            aria-label="Toggle pay period view"
                          />
                          <span className="text-sm text-muted-foreground">Per Pay Period</span>
                        </div>

                        <div className="rounded-md border">
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="px-4 py-3 text-left text-sm font-medium">
                                    Employee
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-medium">
                                    Plan
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-medium w-[220px]">
                                    Coverage Tier
                                  </th>
                                  <th className="px-4 py-3 text-right text-sm font-medium">
                                    Total Premium
                                  </th>
                                  <th className="px-4 py-3 text-right text-sm font-medium">
                                    ER Contribution
                                  </th>
                                  <th className="px-4 py-3 text-right text-sm font-medium">
                                    EE Contribution
                                  </th>
                                  {showPayPeriod && (
                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                      Pay Period
                                    </th>
                                  )}
                                  <th className="px-4 py-3 text-right text-sm font-medium w-[80px]">
                                    History
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(t === coverageTab ? visibleEnrollments : []).map((enr) => {
                                  const emp = clientEmployees.find(
                                    (e) => e.id === (enr as any).employeeId
                                  );
                                  const plan = planById.get(Number((enr as any).planId));
                                  const isOpen = !!expandedRows[(enr as any).id];
                                  const coveredDependents =
                                    emp && plan
                                      ? getCoveredDependentsFor(
                                          Number((enr as any).employeeId),
                                          Number((enr as any).planId)
                                        )
                                      : [];

                                  const result = premiumResultByEnrollmentId.get(
                                    Number((enr as any).id)
                                  );

                                  return (
                                    <React.Fragment key={(enr as any).id}>
                                      <tr
                                        className="border-b hover:bg-muted/25 cursor-pointer"
                                        onClick={() =>
                                          setExpandedRows((prev) => ({
                                            ...prev,
                                            [(enr as any).id]: !prev[(enr as any).id],
                                          }))
                                        }
                                      >
                                        <td className="px-4 py-3 text-sm">
                                          <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                              {isOpen ? (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                              ) : (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                              )}
                                              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                                                <User className="h-4 w-4 text-muted-foreground" />
                                              </div>
                                            </div>
                                            <div className="min-w-0">
                                              <div className="flex items-center gap-2">
                                                {emp ? (
                                                  <Link
                                                    href={
                                                      isEmployerPortal
                                                        ? `/employees/${emp.id}?role=employer`
                                                        : `/clients/${clientId}/employees/${emp.id}`
                                                    }
                                                    className="font-medium hover:underline truncate"
                                                    onClick={(ev) => ev.stopPropagation()}
                                                  >
                                                    {emp.firstName} {emp.lastName}
                                                  </Link>
                                                ) : (
                                                  <span className="font-medium">
                                                    #{(enr as any).employeeId}
                                                  </span>
                                                )}
                                                {coveredDependents.length > 0 && (
                                                  <span className="text-xs text-muted-foreground">
                                                    ({coveredDependents.length} dependents)
                                                  </span>
                                                )}
                                                {result?.warnings?.length > 0 && (
                                                  <TooltipProvider>
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                                      </TooltipTrigger>
                                                      <TooltipContent>
                                                        <p className="text-xs">{result.warnings[0]}</p>
                                                      </TooltipContent>
                                                    </Tooltip>
                                                  </TooltipProvider>
                                                )}
                                              </div>
                                              {emp?.department && (
                                                <div className="text-xs text-muted-foreground truncate">
                                                  {emp.department}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          <div className="flex flex-col gap-1">
                                            <span className="font-medium">
                                              {plan?.name || `Plan #${(enr as any).planId}`}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              {plan?.carrier || ""}
                                            </span>
                                            {isLifeDisabilityType(String((plan as any)?.type || "")) && (
                                              result?.benefitAmount != null ? (
                                                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                  {formatBenefitAmount(result.benefitAmount, result.benefitAmountPeriod)} benefit
                                                </span>
                                              ) : (
                                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                                  ⚠ Coverage not configured
                                                </span>
                                              )
                                            )}
                                            {isFsaPlanType(String((plan as any)?.type || "")) &&
                                              fsaConfigsFetched &&
                                              !isFsaConfigured(
                                                fsaConfigByPlanId.get(Number((plan as any)?.id))
                                              ) && (
                                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                                  ⚠ Coverage not configured
                                                </span>
                                              )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {(() => {
                                            const method = String(
                                              result?.contributionResolved?.method || ""
                                            );
                                            if (!result) {
                                              return (
                                                <span className="text-xs text-muted-foreground">
                                                  Loading…
                                                </span>
                                              );
                                            }
                                            if (method === "table_rates") {
                                              return (
                                                <Badge
                                                  variant="outline"
                                                  className="capitalize bg-background border-border text-foreground shadow-sm"
                                                >
                                                  {result?.contributionResolved?.key ?? "Unknown"}
                                                </Badge>
                                              );
                                            }
                                            const tier = String(
                                              (enr as any)?.coverageTier || ""
                                            ).replace(/_/g, " ");
                                            return tier ? (
                                              <Badge
                                                variant="outline"
                                                className="capitalize bg-background border-border text-foreground shadow-sm"
                                              >
                                                {tier}
                                              </Badge>
                                            ) : (
                                              <span className="text-xs text-muted-foreground">—</span>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm">
                                          {!result ? (
                                            <span className="text-xs text-muted-foreground">
                                              Loading…
                                            </span>
                                          ) : (
                                            <div className="flex flex-col items-end">
                                              <span className="font-medium">
                                                {(() => {
                                                  // When expanded with dependents, show employee-only premium; when collapsed, show total
                                                  if (isOpen && coveredDependents.length > 0) {
                                                    const empOnlyMonthly = plan?.id 
                                                      ? getMonthlyForAgeBand(Number(plan.id), emp?.dateOfBirth, plan, (enr as any).effectiveDate)
                                                      : null;
                                                    return formatCurrencyUtil(empOnlyMonthly ?? 0);
                                                  }
                                                  return formatCurrencyUtil(result.totalMonthlyRate || 0);
                                                })()}
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                per month
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm">
                                          {!result ? (
                                            <span className="text-xs text-muted-foreground">
                                              Loading…
                                            </span>
                                          ) : (
                                            <div className="flex flex-col items-end">
                                              <span className="font-medium">
                                                {formatCurrencyUtil(result.employerPremium || 0)}
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                per month
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm">
                                          {!result ? (
                                            <span className="text-xs text-muted-foreground">
                                              Loading…
                                            </span>
                                          ) : (
                                            <div className="flex flex-col items-end">
                                              <span className="font-medium">
                                                {showPayPeriod
                                                  ? formatCurrencyUtil((result.employeePremium || 0) / getPayPeriodsForEmployee(emp?.payCycle))
                                                  : formatCurrencyUtil(result.employeePremium || 0)
                                                }
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                {showPayPeriod ? "per pay period" : "per month"}
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        {showPayPeriod && (
                                          <td className="px-4 py-3 text-sm">
                                            {emp?.payCycle ? (
                                              <span className="text-muted-foreground">
                                                {emp.payCycle}
                                              </span>
                                            ) : !isPayPeriodConfigured ? (
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <div className="flex items-center gap-1 text-amber-600">
                                                      <AlertTriangle className="h-3.5 w-3.5" />
                                                      <span className="text-xs">Bi-weekly</span>
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    <p>No pay period configured for this employee or company.</p>
                                                    <p className="text-xs text-muted-foreground">Using default: Bi-weekly</p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            ) : (
                                              <span className="text-muted-foreground">—</span>
                                            )}
                                          </td>
                                        )}
                                        <td className="px-4 py-3 text-right text-sm">
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="outline"
                                                  size="icon"
                                                  className="h-8 w-8 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 dark:border-indigo-900 dark:hover:bg-indigo-950/40"
                                                  onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    setHistoryEnrollmentId(Number((enr as any).id));
                                                    setIsHistoryOpen(true);
                                                  }}
                                                >
                                                  <History className="h-4 w-4 text-indigo-600" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>View change history</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        </td>
                                      </tr>

                                      {isOpen &&
                                        (coveredDependents || []).map((d: any) => {
                                          const depEnr = getDependentEnrollmentRow(
                                            Number((enr as any).employeeId),
                                            Number((enr as any).planId),
                                            Number(d.id)
                                          );
                                          const depMonthly = plan?.id
                                            ? getMonthlyForAgeBand(
                                                Number(plan.id),
                                                d?.dateOfBirth,
                                                plan,
                                                (enr as any).effectiveDate
                                              )
                                            : null;
                                          return (
                                            <tr
                                              key={`dep-${(enr as any).id}-${d.id}`}
                                              className="border-b bg-muted/25"
                                            >
                                              <td className="px-4 py-3 text-sm">
                                                <div className="flex items-center gap-3 pl-10">
                                                  <div className="h-8 w-8 rounded-full bg-background border flex items-center justify-center">
                                                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                                                  </div>
                                                  <div className="min-w-0">
                                                    <div className="font-medium truncate">
                                                      {d.firstName} {d.lastName}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                      {d.relationship && (
                                                        <Badge
                                                          variant="secondary"
                                                          className="h-5 px-2 capitalize"
                                                        >
                                                          {String(d.relationship)}
                                                        </Badge>
                                                      )}
                                                      <span>
                                                        Age: {calcAge(d.dateOfBirth)}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-sm">
                                                <div className="flex flex-col gap-1">
                                                  <span className="font-medium">
                                                    {plan?.name ||
                                                      `Plan #${(enr as any).planId}`}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground">
                                                    {plan?.carrier || ""}
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-sm">
                                                <Badge
                                                  variant="outline"
                                                  className="capitalize bg-background border-border text-foreground shadow-sm"
                                                >
                                                  Dependent
                                                </Badge>
                                              </td>
                                              <td className="px-4 py-3 text-right text-sm">
                                                <div className="flex flex-col items-end">
                                                  <span className="font-medium">
                                                    {formatCurrencyUtil(depMonthly ?? 0)}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground">
                                                    per month
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-right text-sm">
                                                <div className="flex flex-col items-end">
                                                  <span className="font-medium">
                                                    {formatCurrencyUtil(0)}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground">
                                                    per month
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-right text-sm">
                                                <div className="flex flex-col items-end">
                                                  <span className="font-medium">
                                                    {formatCurrencyUtil(0)}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground">
                                                    {showPayPeriod ? "per pay period" : "per month"}
                                                  </span>
                                                </div>
                                              </td>
                                              {showPayPeriod && (
                                                <td className="px-4 py-3 text-sm">
                                                  <span className="text-muted-foreground">—</span>
                                                </td>
                                              )}
                                              <td className="px-4 py-3 text-right text-sm">
                                                {/* Actions column - intentionally empty for dependent rows */}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {t === coverageTab && totalForTab > 0 && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="text-xs text-muted-foreground">
                              Showing <span className="font-medium">{start}</span>-
                              <span className="font-medium">{end}</span> of{" "}
                              <span className="font-medium">{totalForTab}</span>
                            </div>
                            <div className="flex items-center gap-2 justify-end">
                              <Select
                                value={String(pageSize)}
                                onValueChange={(v) => {
                                  handlePageSizeChange(v);
                                }}
                              >
                                <SelectTrigger className="h-8 w-[110px]">
                                  <SelectValue placeholder={`${pageSize} / page`} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="25">25 / page</SelectItem>
                                  <SelectItem value="50">50 / page</SelectItem>
                                  <SelectItem value="100">100 / page</SelectItem>
                                  <SelectItem value="200">200 / page</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(clampedPage - 1)}
                                disabled={clampedPage <= 1}
                              >
                                Prev
                              </Button>
                              <div className="text-xs text-muted-foreground min-w-[90px] text-center">
                                Page{" "}
                                <span className="font-medium">{clampedPage}</span> of{" "}
                                <span className="font-medium">{totalPages}</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(clampedPage + 1)}
                                disabled={clampedPage >= totalPages}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}

                        {t === coverageTab && (
                          <div className="pt-6 border-t border-dashed">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <User className="h-4 w-4" />
                              <span>
                                Employees Who Waived {label(coverageTab).toLowerCase()} Coverage
                              </span>
                              <Badge variant="secondary" className="h-5 px-2">
                                {filteredWaivedEmployeesForTab.length}
                              </Badge>
                            </div>

                            <div className="mt-3">
                              {filteredWaivedEmployeesForTab.length === 0 ? (
                                <div className="text-sm text-muted-foreground italic">
                                  No waived employees
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {filteredWaivedEmployeesForTab.map((w: any) => (
                                    <div
                                      key={`waived-${w.employeeId}`}
                                      className="border rounded-lg p-3 flex items-center justify-between gap-4 max-w-[520px]"
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                                          <User className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-medium truncate">
                                            {w.employee
                                              ? `${w.employee.firstName} ${w.employee.lastName}`
                                              : `#${w.employeeId}`}
                                          </div>
                                          <div className="text-xs text-muted-foreground truncate">
                                            {w.employee?.department || ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <Badge variant="secondary" className="h-6 px-2">
                                          Waived
                                        </Badge>
                                        <div className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                                          {w.waiverReason ? w.waiverReason : "\u00A0"}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground pt-4">
                              <div>Participation Rate: {participationRateForTab}%</div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const rows = (filteredWaivedEmployeesForTab as any[]).map((w) => ({
                                    Employee: w.employee
                                      ? `${w.employee.firstName} ${w.employee.lastName}`
                                      : `#${w.employeeId}`,
                                    Department: w.employee?.department || "",
                                    "Waiver Reason": w.waiverReason || "",
                                  }));
                                  exportToCSV(rows, {
                                    filename: `waivers_${coverageTab}_${new Date()
                                      .toISOString()
                                      .slice(0, 10)}`,
                                    toast: { success: "Waiver list exported" },
                                  });
                                }}
                                disabled={filteredWaivedEmployeesForTab.length === 0}
                              >
                                Export Waiver List
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            );
          })()}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              No enrollments yet
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
              Enrollments track employee benefit selections and coverage.
              Enrollment starts from an employee's Benefits tab — pick an
              employee to enroll them in the plans offered here.
            </p>
            {showCreate && (
              <Button
                size="lg"
                onClick={() =>
                  // Enrollment is created per employee (Employees > employee >
                  // Benefits). Send the user there for this client or agency.
                  navigate(
                    clientIdNum != null
                      ? `/clients/${clientIdNum}?tab=employees`
                      : "/agency?tab=employees"
                  )
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Enroll Employees
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      {/* Edit Enrollment Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-xl" aria-describedby="edit-enrollment-description">
          <DialogHeader>
            <DialogTitle>Edit Enrollment</DialogTitle>
          </DialogHeader>
          <p id="edit-enrollment-description" className="hidden">
            Edit enrollment details for employee benefits
          </p>
          <div className="space-y-4">
            <div>
              <Label>Plan</Label>
              <Popover open={planOpen} onOpenChange={setPlanOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between mt-1"
                  >
                    {(() => {
                      const plan = plansData.find(
                        (p) => p.id === (editing as any)?.planId
                      ) as any;
                      return plan
                        ? `${plan.name} (${plan.type})`
                        : "Choose plan";
                    })()}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search plans" />
                    <CommandEmpty>No plans found.</CommandEmpty>
                    <CommandList>
                      <CommandGroup>
                        {filteredPlanOptions.map((p) => {
                          const today = new Date();
                          const effectiveDate = (p as any).effectiveDate
                            ? parseDateLocal((p as any).effectiveDate)
                            : null;
                          const isFuture = effectiveDate && effectiveDate > today;

                          return (
                            <CommandItem
                              key={p.id}
                              value={`${p.name} ${p.type}`}
                              onSelect={() =>
                                setEditing((prev) => ({
                                  ...(prev as any),
                                  planId: p.id,
                                }))
                              }
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  (editing as any)?.planId === p.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              <div className="flex items-center gap-2 flex-1">
                                <span>{p.name} ({p.type})</span>
                                {isFuture && (
                                  <Badge variant="secondary" className="text-xs">
                                    Starts {effectiveDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </Badge>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Coverage Tier</Label>
              {editIsTableMethod ? (
                <div className="mt-1 text-sm text-muted-foreground">
                  This plan uses table-based contributions; tier is determined
                  by covered members.
                </div>
              ) : (
                <>
                  <Select
                    value={editCoverageTier || "employee_only"}
                    onValueChange={setEditCoverageTier}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select coverage tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee_only">Employee Only</SelectItem>
                      <SelectItem value="employee_spouse">
                        Employee + Spouse
                      </SelectItem>
                      <SelectItem value="employee_children">
                        Employee + Children
                      </SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>

            <EditDependentsSelector
              employeeId={(editing as any)?.employeeId}
              planId={(editing as any)?.planId}
              onChange={(ids) =>
                setEditing((prev) => ({
                  ...(prev as any),
                  _selectedDependentIds: ids,
                }))
              }
            />

            <div>
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={editEffectiveDate}
                onChange={(e) => setEditEffectiveDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  id="waivedCoverage"
                  type="checkbox"
                  checked={editWaived}
                  onChange={(e) => setEditWaived(e.target.checked)}
                />
                <Label htmlFor="waivedCoverage">Waive Coverage</Label>
              </div>
            </div>

            <div>
              <Label>Beneficiary Name</Label>
              <Input
                value={editBeneficiaryName}
                onChange={(e) => setEditBeneficiaryName(e.target.value)}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label>Beneficiary Relationship</Label>
              <Input
                value={editBeneficiaryRelationship}
                onChange={(e) => setEditBeneficiaryRelationship(e.target.value)}
                placeholder="Optional"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!editing) return;
                  (async () => {
                    if (editSaving) return;
                    setEditSaving(true);
                    try {
                      const employeeId = (editing as any)?.employeeId as
                        | number
                        | undefined;
                      const targetPlanId = (editing as any)?.planId as
                        | number
                        | undefined;

                      const updates = {
                        planId: targetPlanId,
                        coverageTier: editCoverageTier,
                        effectiveDate: (parseDateLocal(editEffectiveDate) ?? new Date()).toISOString(),
                        waivedCoverage: editWaived,
                        beneficiaryName: editBeneficiaryName || null,
                        beneficiaryRelationship:
                          editBeneficiaryRelationship || null,
                      } as any;

                      await updateEnrollment.mutateAsync(updates);

                      // Sync dependent enrollment rows to match checkbox selections.
                      // Treat waived dep rows as "not currently enrolled" so the diff
                      // matches what the broker sees in the dialog.
                      if (employeeId && targetPlanId) {
                        const res = await fetch(
                          `/api/enrollments?employeeId=${employeeId}`
                        );
                        const rows = res.ok ? await res.json() : [];
                        const currentForPlan = (rows as any[]).filter((r) =>
                          isActiveDependentEnrollmentForPlan(r, targetPlanId),
                        );

                        const desiredIds: number[] = Array.isArray(
                          (editing as any)?._selectedDependentIds
                        )
                          ? ((editing as any)?._selectedDependentIds as number[])
                          : currentForPlan.map((r) => r.dependentId as number);

                        const desired = new Set(desiredIds);

                        // Delete removed
                        await Promise.all(
                          currentForPlan
                            .filter((r) => !desired.has(r.dependentId as number))
                            .map((r) =>
                              fetch(`/api/enrollments/${r.id}`, {
                                method: "DELETE",
                              })
                            )
                        );

                        // Create missing
                        const existingSet = new Set(
                          currentForPlan.map((r) => r.dependentId as number)
                        );
                        const toAdd = desiredIds.filter(
                          (id) => !existingSet.has(id)
                        );
                        const createResponses = await Promise.all(
                          toAdd.map((depId) =>
                            fetch(`/api/enrollments`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                employeeId,
                                clientId: (editing as any)?.clientId,
                                planId: targetPlanId,
                                dependentId: depId,
                                coverageTier: editCoverageTier,
                                effectiveDate: (parseDateLocal(
                                  editEffectiveDate
                                ) ?? new Date()).toISOString(),
                                enrollmentSource: "open_enrollment",
                              }),
                            })
                          )
                        );
                        const failed = createResponses.find((r) => !r.ok);
                        if (failed) throw new Error("Failed to add dependents");
                      }

                      // Refetch enrollments AFTER dependent sync so dependents + premiums update immediately.
                      queryClient.invalidateQueries({
                        queryKey: ["/api/enrollments", clientId],
                      });
                      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
                      if (employeeId) {
                        queryClient.invalidateQueries({
                          queryKey: ["/api/enrollments", employeeId],
                        });
                      }

                      toast({ title: "Enrollment updated", variant: "success" });
                      setIsEditOpen(false);
                      setEditing(null);
                    } catch (e: any) {
                      // updateEnrollment has its own error toast; handle sync errors here
                      if (
                        e?.message &&
                        String(e.message) !== "Failed to add dependents"
                      ) {
                        // leave default
                      } else if (String(e?.message) === "Failed to add dependents") {
                        toast({
                          title: "Failed to update dependents",
                          variant: "destructive",
                        });
                      }
                    } finally {
                      setEditSaving(false);
                    }
                  })();
                }}
                disabled={editSaving || updateEnrollment.isPending}
              >
                {editSaving || updateEnrollment.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <EnrollmentDetailsPortal
        clientEnrollments={clientEnrollments as any[]}
        plansData={plansData as any[]}
        clientEmployees={clientEmployees as any[]}
      />
      <FutureElectionDialog
        open={oeElectionOpen}
        onOpenChange={setOeElectionOpen}
        clientEmployees={clientEmployees as any[]}
        clientEnrollments={clientEnrollments as any[]}
        plansData={plansData as any[]}
        clientId={clientId}
      />
      {clientIdNum != null && (
        <BulkEnrollmentFormsDialog
          open={bulkFormsOpen}
          onOpenChange={setBulkFormsOpen}
          clientId={clientIdNum}
          planOptions={bulkFormPlanOptions}
        />
      )}
      <EnrollmentHistoryDialog
        enrollmentId={historyEnrollmentId}
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
      />
    </div>
  );
};

/**
 * Broker-made future election (Open Enrollment): applies a member's NEXT
 * plan-year choice ahead of time without touching current coverage.
 * Routes to the prospective-change machinery — switch plan / change tier end
 * the current row AT the future date and start the new one there (the ledger
 * keeps billing current coverage until then); a brand-new election posts a
 * future-effective enrollment with source open_enrollment.
 */
function FutureElectionDialog({
  open,
  onOpenChange,
  clientEmployees,
  clientEnrollments,
  plansData,
  clientId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  clientEmployees: any[];
  clientEnrollments: any[];
  plansData: any[];
  clientId?: string | number;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [employeeId, setEmployeeId] = useState<string>("");
  const [action, setAction] = useState<"switch_plan" | "change_tier" | "new_enrollment">("switch_plan");
  const [baseEnrollmentId, setBaseEnrollmentId] = useState<string>("");
  const [targetPlanId, setTargetPlanId] = useState<string>("");
  const [newTier, setNewTier] = useState<string>("employee_only");
  const [effectiveDate, setEffectiveDate] = useState<string>("");

  const employee = clientEmployees.find((e) => String(e.id) === employeeId) || null;
  const currentRows = (clientEnrollments as any[]).filter((e) => {
    if (String(e.employeeId) !== employeeId || e.dependentId != null) return false;
    if (e.waivedCoverage) return false;
    const s = String(e.status || "").toLowerCase();
    return s === "active" || s === "pending";
  });
  const baseRow = currentRows.find((e) => String(e.id) === baseEnrollmentId) || null;
  const planName = (id: any) => (plansData.find((p) => p.id === Number(id)) as any)?.name || `Plan #${id}`;

  // Default the effective date to the next plan-year anniversary of the
  // selected current enrollment (or Jan 1 next year for new elections).
  useEffect(() => {
    const src = baseRow?.effectiveDate ? parseDateLocal(baseRow.effectiveDate) : null;
    const next = src
      ? new Date(src.getFullYear() + 1, src.getMonth(), src.getDate())
      : new Date(new Date().getFullYear() + 1, 0, 1);
    // Anniversary already passed → move to the following year.
    while (next <= new Date()) next.setFullYear(next.getFullYear() + 1);
    setEffectiveDate(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`
    );
  }, [baseEnrollmentId, employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useMutation({
    mutationFn: async () => {
      if (!employee) throw new Error("Select an employee");
      if (!effectiveDate) throw new Error("Effective date is required");
      if (parseDateLocal(effectiveDate)! <= new Date())
        throw new Error("A future election must have a future effective date");
      if (action === "switch_plan") {
        if (!baseRow) throw new Error("Select the current enrollment to replace");
        if (!targetPlanId) throw new Error("Select the new plan");
        const res = await fetch(`/api/enrollments/${baseRow.id}/change-plan`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ newPlanId: Number(targetPlanId), effectiveDate, reason: "open_enrollment" }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.message || "Failed to record the election");
        return body;
      }
      if (action === "change_tier") {
        if (!baseRow) throw new Error("Select the current enrollment");
        const res = await fetch(`/api/enrollments/${baseRow.id}/change-tier`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ newTier, effectiveDate, reason: "open_enrollment" }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.message || "Failed to record the election");
        return body;
      }
      // New enrollment for a benefit type without current coverage.
      if (!targetPlanId) throw new Error("Select the plan");
      const res = await fetch(`/api/employees/${employee.id}/enrollments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          planId: Number(targetPlanId),
          clientId: employee.clientId ?? clientId,
          coverageTier: newTier,
          effectiveDate,
          enrollmentSource: "open_enrollment",
          status: "pending",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to record the election");
      return body;
    },
    onSuccess: () => {
      toast({
        title: "Future election recorded",
        description: `Takes effect ${effectiveDate}. Current coverage continues until then.`,
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments", clientId] });
      onOpenChange(false);
      setEmployeeId("");
      setBaseEnrollmentId("");
      setTargetPlanId("");
    },
    onError: (e: any) => {
      toast({ title: e?.message || "Failed to record the election", variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="future-election-description">
        <DialogHeader>
          <DialogTitle>Future Election (Open Enrollment)</DialogTitle>
        </DialogHeader>
        <p id="future-election-description" className="text-sm text-muted-foreground">
          Records a member's next-plan-year choice ahead of time. Current coverage keeps billing
          until the effective date; the new election starts there.
        </p>
        <div className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={(v) => { setEmployeeId(v); setBaseEnrollmentId(""); }}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {clientEmployees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>{e.firstName} {e.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Election</Label>
            <Select value={action} onValueChange={(v) => setAction(v as any)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="switch_plan">Switch to a different plan</SelectItem>
                <SelectItem value="change_tier">Change coverage tier</SelectItem>
                <SelectItem value="new_enrollment">New enrollment (no current coverage)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(action === "switch_plan" || action === "change_tier") && (
            <div>
              <Label>Current enrollment</Label>
              <Select value={baseEnrollmentId} onValueChange={setBaseEnrollmentId} disabled={!employeeId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={employeeId ? (currentRows.length ? "Select current coverage" : "No current coverage") : "Select an employee first"} />
                </SelectTrigger>
                <SelectContent>
                  {currentRows.map((e) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {planName(e.planId)} · {String(e.coverageTier || "employee_only").replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(action === "switch_plan" || action === "new_enrollment") && (
            <div>
              <Label>{action === "switch_plan" ? "New plan" : "Plan"}</Label>
              <Select value={targetPlanId} onValueChange={setTargetPlanId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>
                  {plansData
                    .filter((p: any) => !baseRow || p.id !== baseRow.planId)
                    .map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name} ({p.type})</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(action === "change_tier" || action === "new_enrollment") && (
            <div>
              <Label>Coverage tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee_only">Employee Only</SelectItem>
                  <SelectItem value="employee_spouse">Employee + Spouse</SelectItem>
                  <SelectItem value="employee_children">Employee + Children</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Effective date (next plan year)</Label>
            <Input type="date" className="mt-1" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? "Recording…" : "Record Election"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AsyncEnrollValue({
  clientId,
  plan,
  employee,
  dependents,
  coverageTier,
  pick,
}: {
  clientId?: string | number;
  plan: any;
  employee: any;
  dependents: any[];
  coverageTier: string;
  pick: (r: any) => number;
}) {
  const [val, setVal] = React.useState<number>(0);
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!plan || !employee) {
        setVal(0);
        return;
      }
      // Rates + contributions for this plan AND its referenced base plan.
      const effectiveClientId = clientId ?? employee?.clientId;
      const pd = await fetchPlanPremiumData([plan.id], effectiveClientId);
      const planRates: PlanRate[] = (pd.ratesByPlanId[plan.id] || []) as PlanRate[];
      const planContribs = pd.contribsByPlanId[plan.id] || [];

      // Determine covered dependents for this employee+plan when not provided
      let coveredDependents = Array.isArray(dependents) ? dependents : [];
      if (coveredDependents.length === 0 && employee?.id && plan?.id) {
        try {
          const [empEnrollmentsRes, employeeDependentsRes] = await Promise.all([
            fetch(`/api/enrollments?employeeId=${employee.id}`),
            fetch(`/api/employees/${employee.id}/dependents`),
          ]);
          const empEnrollments = empEnrollmentsRes.ok
            ? await empEnrollmentsRes.json()
            : [];
          const allEmployeeDependents = employeeDependentsRes.ok
            ? await employeeDependentsRes.json()
            : [];
          const enrolledDepIds = (empEnrollments as any[])
            .filter((r) => isActiveDependentEnrollmentForPlan(r, plan.id))
            .map((r) => r.dependentId as number);
          coveredDependents = (allEmployeeDependents as any[]).filter((d) =>
            enrolledDepIds.includes(d.id)
          );
        } catch {}
      }
      // Determine age reference date per rule:
      // If employee was hired before plan effective date, age as of plan effective date.
      // Otherwise, age as of hire date.
      const planEffective = plan?.effectiveDate
        ? parseDateLocal(plan.effectiveDate)
        : null;
      const hireDate = employee?.hireDate ? parseDateLocal(employee.hireDate) : null;
      let ageAsOf: Date | undefined = undefined;
      if (planEffective && hireDate) {
        ageAsOf = hireDate < planEffective ? planEffective : hireDate;
      } else if (planEffective) {
        ageAsOf = planEffective;
      } else if (hireDate) {
        ageAsOf = hireDate;
      }

      const result = calculateEnrollmentPremium({
        plan,
        planRates,
        planContributions: planContribs as any,
        employee,
        dependents: coveredDependents,
        coverageTier,
        ageAsOf,
        allPlanContributions: pd.allPlanContributions,
        allPlanRates: pd.allPlanRates,
      }) as any;
      if (!mounted) return;
      setVal(pick(result) || 0);
    };
    run();
    return () => {
      mounted = false;
    };
  }, [plan?.id, employee?.id, coverageTier, JSON.stringify(dependents)]);
  return <>{formatCurrencyUtil(val)}</>;
}

function AsyncTierOrTableLabel({
  clientId,
  plan,
  employee,
  dependents,
  coverageTier,
}: {
  clientId?: string | number;
  plan: any;
  employee: any;
  dependents: any[];
  coverageTier: string;
}) {
  const [label, setLabel] = React.useState<{ method: string; text: string }>({
    method: "",
    text: "",
  });
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!plan || !employee) {
        setLabel({ method: "", text: "" });
        return;
      }
      const effectiveClientId = clientId ?? employee?.clientId;
      const pd = await fetchPlanPremiumData([plan.id], effectiveClientId);
      const planRates: PlanRate[] = (pd.ratesByPlanId[plan.id] || []) as PlanRate[];
      const planContribs = pd.contribsByPlanId[plan.id] || [];

      // Determine covered dependents for this employee+plan when not provided
      let coveredDependents = Array.isArray(dependents) ? dependents : [];
      if (coveredDependents.length === 0 && employee?.id && plan?.id) {
        try {
          const [empEnrollmentsRes, employeeDependentsRes] = await Promise.all([
            fetch(`/api/enrollments?employeeId=${employee.id}`),
            fetch(`/api/employees/${employee.id}/dependents`),
          ]);
          const empEnrollments = empEnrollmentsRes.ok
            ? await empEnrollmentsRes.json()
            : [];
          const allEmployeeDependents = employeeDependentsRes.ok
            ? await employeeDependentsRes.json()
            : [];
          const enrolledDepIds = (empEnrollments as any[])
            .filter((r) => isActiveDependentEnrollmentForPlan(r, plan.id))
            .map((r) => r.dependentId as number);
          coveredDependents = (allEmployeeDependents as any[]).filter((d) =>
            enrolledDepIds.includes(d.id)
          );
        } catch {}
      }

      const result = calculateEnrollmentPremium({
        plan,
        planRates,
        planContributions: planContribs as any,
        employee,
        dependents: coveredDependents,
        coverageTier,
        allPlanContributions: pd.allPlanContributions,
        allPlanRates: pd.allPlanRates,
      }) as any;
      if (!mounted) return;
      const method = (result?.contributionResolved?.method || "").toString();
      if (method === "table_rates") {
        setLabel({
          method: "table",
          text: result?.contributionResolved?.key ?? "Unknown",
        });
      } else if (method === "tier_rates") {
        setLabel({ method: "tier", text: coverageTier?.replace(/_/g, " ") });
      } else {
        setLabel({ method: "tier", text: coverageTier?.replace(/_/g, " ") });
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [plan?.id, employee?.id, coverageTier, JSON.stringify(dependents)]);

  if (!label.text) return null;

  return (
    <div className="flex items-center gap-2">
      <Badge variant="secondary" className="capitalize">
        {label.text}
      </Badge>
      {label.method === "table" && (
        <span className="text-xs text-muted-foreground">Table Rate</span>
      )}
    </div>
  );
}

function AsyncDependentCount({
  employeeId,
  planId,
}: {
  employeeId: number;
  planId: number;
}) {
  const [count, setCount] = React.useState<number>(0);
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!employeeId || !planId) {
        setCount(0);
        return;
      }
      try {
        const [empEnrollmentsRes] = await Promise.all([
          fetch(`/api/enrollments?employeeId=${employeeId}`),
        ]);
        const empEnrollments = empEnrollmentsRes.ok
          ? await empEnrollmentsRes.json()
          : [];
        const num = (empEnrollments as any[]).filter((r) =>
          isActiveDependentEnrollmentForPlan(r, planId),
        ).length;
        if (!mounted) return;
        setCount(num);
      } catch {
        if (!mounted) return;
        setCount(0);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [employeeId, planId]);
  return <>{count}</>;
}

function AsyncDependentList({
  employeeId,
  planId,
}: {
  employeeId: number;
  planId: number;
}) {
  const [list, setList] = React.useState<Array<{ id: number; label: string }>>(
    []
  );
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const [empEnrollmentsRes, depsRes] = await Promise.all([
          fetch(`/api/enrollments?employeeId=${employeeId}`),
          fetch(`/api/employees/${employeeId}/dependents`),
        ]);
        const enr = empEnrollmentsRes.ok ? await empEnrollmentsRes.json() : [];
        const deps = depsRes.ok ? await depsRes.json() : [];
        const enrolledDepIds = (enr as any[])
          .filter((r) => r.planId === planId && Number.isFinite(r.dependentId))
          .map((r) => r.dependentId as number);

        // Helper to calculate age
        const calcAge = (dob?: string | Date | null) => {
          if (!dob) return 0;
          const d = parseDateLocal(dob as any);
          if (!d || Number.isNaN(d.getTime())) return 0;
          const now = new Date();
          let age = now.getFullYear() - d.getFullYear();
          const m = now.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
          return Math.max(0, age);
        };

        const items = (deps as any[])
          .filter((d) => enrolledDepIds.includes(d.id))
          .map((d) => {
            const age = calcAge(d.dateOfBirth);
            return {
              id: d.id as number,
              label: `${d.firstName} ${d.lastName} (${d.relationship}, Age: ${age})`,
            };
          });
        if (!mounted) return;
        setList(items);
      } catch {
        if (!mounted) return;
        setList([]);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [employeeId, planId]);
  if (list.length === 0)
    return <div className="text-sm text-muted-foreground italic">No dependents enrolled</div>;
  return (
    <div className="space-y-2">
      {list.map((d) => (
        <div key={d.id} className="flex items-center gap-2 text-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
          <span>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function EditDependentsSelector({
  employeeId,
  planId,
  onChange,
}: {
  employeeId?: number | null;
  planId?: number | null;
  onChange: (ids: number[]) => void;
}) {
  const [options, setOptions] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<number[]>([]);
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!employeeId || !planId) return;
      try {
        const [depsRes, enrRes] = await Promise.all([
          fetch(`/api/employees/${employeeId}/dependents`),
          fetch(`/api/enrollments?employeeId=${employeeId}`),
        ]);
        const deps = depsRes.ok ? await depsRes.json() : [];
        const enr = enrRes.ok ? await enrRes.json() : [];
        const current = (enr as any[])
          .filter((r) => r.planId === planId && Number.isFinite(r.dependentId))
          .map((r) => r.dependentId as number);
        if (!mounted) return;
        setOptions(deps as any[]);
        setSelected(current);
        onChange(current);
      } catch {}
    };
    run();
    return () => {
      mounted = false;
    };
  }, [employeeId, planId]);
  if (!employeeId || !planId) return null;
  return (
    <div>
      <Label>Dependents</Label>
      <div className="border rounded p-2 max-h-48 overflow-y-auto mt-1">
        {(options || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">None</div>
        ) : (
          (options || []).map((d) => {
            const id = d.id as number;
            const checked = selected.includes(id);
            return (
              <label
                key={id}
                className="flex items-center justify-between gap-2 py-1 text-sm"
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(ev) => {
                      const next = ev.target.checked
                        ? Array.from(new Set([...(selected || []), id]))
                        : (selected || []).filter((x) => x !== id);
                      setSelected(next);
                      onChange(next);
                    }}
                  />
                  <span>
                    {d.firstName} {d.lastName}{" "}
                    <span className="text-muted-foreground">
                      ({d.relationship})
                    </span>
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function DetailsButton({ enrollment }: { enrollment: any }) {
  const { setDetailsContext } = useEnrollmentDetails();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() =>
        setDetailsContext({
          employeeId: enrollment.employeeId,
          planId: enrollment.planId,
          effectiveDate: enrollment.effectiveDate,
        })
      }
    >
      View
    </Button>
  );
}

// Lightweight context via module-level state for details dialog
let _detailsContext: any = null;
let _setDetailsOpen: ((open: boolean) => void) | null = null;
function useEnrollmentDetails() {
  return {
    setDetailsContext: (ctx: any) => {
      _detailsContext = ctx;
      if (_setDetailsOpen) _setDetailsOpen(true);
    },
  };
}

function EnrollmentDetailsPortal({
  clientEnrollments,
  plansData,
  clientEmployees,
}: {
  clientEnrollments: any[];
  plansData: any[];
  clientEmployees: any[];
}) {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<any>(null);
  _setDetailsOpen = (o: boolean) => setOpen(o);
  if (open && ctx !== _detailsContext) setCtx(_detailsContext);

  const employeeId = ctx?.employeeId as number | undefined;
  const planId = ctx?.planId as number | undefined;

  const { data: employeeEnrollments = [], isLoading: empEnrLoading } = useQuery<
    any[]
  >({
    queryKey: ["/api/enrollments", employeeId],
    enabled: !!open && !!employeeId,
    queryFn: async () => {
      const res = await fetch(`/api/enrollments?employeeId=${employeeId}`);
      if (!res.ok) throw new Error("Failed to fetch employee enrollments");
      return res.json();
    },
  });

  const { data: dependents = [], isLoading: depsLoading } = useQuery<any[]>({
    queryKey: [`/api/employees/${employeeId}/dependents`],
    enabled: !!open && !!employeeId,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/dependents`);
      if (!res.ok) throw new Error("Failed to fetch dependents");
      return res.json();
    },
  });

  const enrolledDepIds = (employeeEnrollments as any[])
    .filter((r) => r.planId === planId && Number.isFinite(r.dependentId))
    .map((r) => r.dependentId as number);
  const enrolledDependents = (dependents as any[]).filter((d) =>
    enrolledDepIds.includes(d.id)
  );

  const plan = plansData.find((p) => p.id === planId);
  const employee = clientEmployees.find((e) => e.id === employeeId);

  // Helper to calculate age
  const calcAge = (dob?: string | Date | null) => {
    if (!dob) return 0;
    const d = parseDateLocal(dob as any);
    if (!d || Number.isNaN(d.getTime())) return 0;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return Math.max(0, age);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" aria-describedby="enrollment-details-description">
        <DialogHeader>
          <DialogTitle>Enrollment Details</DialogTitle>
        </DialogHeader>
        <p id="enrollment-details-description" className="hidden">
          View detailed enrollment information
        </p>
        {!ctx ? (
          <div className="py-4 text-sm text-muted-foreground">No selection</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3 rounded-lg bg-muted/30 p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Employee</div>
                  <div className="font-medium">
                    {employee
                      ? `${employee.firstName} ${employee.lastName}`
                      : `#${employeeId}`}
                  </div>
                </div>
              </div>
              <Separator />
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Plan</div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{plan?.name || `#${planId}`}</span>
                  {plan && <Badge variant="outline" className="text-xs">{plan.type}</Badge>}
                </div>
              </div>
              <Separator />
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Effective Date</div>
                <div className="font-medium">
                  {ctx.effectiveDate
                    ? formatDateDisplay(ctx.effectiveDate)
                    : "-"}
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Covered Dependents</div>
              {empEnrLoading || depsLoading ? (
                <div className="text-sm text-muted-foreground italic">Loading…</div>
              ) : enrolledDependents.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No dependents enrolled</div>
              ) : (
                <div className="space-y-2">
                  {enrolledDependents.map((d) => {
                    const age = calcAge(d.dateOfBirth);
                    return (
                      <div key={d.id} className="flex items-center gap-2 text-sm p-2 rounded-md bg-muted/50">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                        <span>
                          {d.firstName} {d.lastName} ({d.relationship}, Age: {age})
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EnrollmentsTab;
